import { useEffect } from 'react';
import { Link } from 'react-router-dom';
import Header from '../../home/components/Header';
import Footer from '../../home/components/Footer';
import BlogRelatedStrip from '../../../components/feature/BlogRelatedStrip';
import BlogProgressBar from '../../../components/feature/BlogProgressBar';

export default function BusinessKeyPersonInsuranceIndianapolisBlog() {
  const SITE_URL = import.meta.env.VITE_SITE_URL || 'https://hoosierlifeinsurance.com';

  useEffect(() => {
    // Inject Schema.org JSON-LD
    const schemas = [
      {
        '@context': 'https://schema.org',
        '@type': 'Article',
        headline: 'Key Person Insurance Indianapolis: Protect Your Business from Loss',
        description: 'Learn how key person insurance in Indianapolis protects your business if a critical employee or owner passes away. Get coverage options and tax benefits explained.',
        author: {
          '@type': 'Person',
          name: 'Benjamin Wright'
        },
        publisher: {
          '@type': 'Organization',
          name: 'Hoosier Life Insurance',
          url: SITE_URL
        },
        datePublished: '2024-11-10',
        dateModified: new Date().toISOString(),
        url: `${SITE_URL}/blog/business-key-person-insurance-indianapolis`,
        mainEntityOfPage: `${SITE_URL}/blog/business-key-person-insurance-indianapolis`,
        image: { '@type': 'ImageObject', url: 'https://public.readdy.ai/ai/img_res/d5bf279c0e40c9a9611eca2ac952ff7f.jpg', width: 1200, height: 630 },
        timeRequired: 'PT11M',
        wordCount: 2200,
        inLanguage: 'en-US'
      },
      {
        '@context': 'https://schema.org',
        '@type': 'BreadcrumbList',
        itemListElement: [
          {
            '@type': 'ListItem',
            position: 1,
            name: 'Home',
            item: SITE_URL
          },
          {
            '@type': 'ListItem',
            position: 2,
            name: 'Blog',
            item: `${SITE_URL}/blog`
          },
          {
            '@type': 'ListItem',
            position: 3,
            name: 'Key Person Insurance Indianapolis',
            item: `${SITE_URL}/blog/business-key-person-insurance-indianapolis`
          }
        ]
      },
      {
        '@context': 'https://schema.org',
        '@type': 'WebPage',
        name: 'Key Person Insurance Indianapolis: Protect Your Business from Loss',
        description: 'Learn how key person insurance in Indianapolis protects your business if a critical employee or owner passes away. Get coverage options and tax benefits explained.',
        url: `${SITE_URL}/blog/business-key-person-insurance-indianapolis`,
        inLanguage: 'en-US',
        isPartOf: {
          '@type': 'WebSite',
          url: SITE_URL
        },
        speakable: {
          '@type': 'SpeakableSpecification',
          cssSelector: ['h1', 'h2', 'details summary']
        }
      },
      {
        '@context': 'https://schema.org',
        '@type': 'FAQPage',
        mainEntity: [
          {
            '@type': 'Question',
            name: 'Does the key person need to consent to the policy?',
            acceptedAnswer: {
              '@type': 'Answer',
              text: 'Yes. The insured person must consent to the policy, undergo a medical exam, and sign the application. They have no ownership rights to the policy — the business owns it, pays premiums, and receives the death benefit if the key person passes away.'
            }
          },
          {
            '@type': 'Question',
            name: 'What happens if the key person leaves the company?',
            acceptedAnswer: {
              '@type': 'Answer',
              text: 'You have several options: (1) Cancel the policy and stop paying premiums, (2) Convert it to a policy on a different key employee if the carrier allows, or (3) Transfer ownership to the departing employee as part of their severance package. Most Indianapolis businesses cancel the policy when the insured person leaves.'
            }
          },
          {
            '@type': 'Question',
            name: 'Can I have key person insurance on multiple employees?',
            acceptedAnswer: {
              '@type': 'Answer',
              text: 'Absolutely. Many Indianapolis businesses insure 2–5 key people — founders, top salespeople, lead engineers, etc. Each person gets their own policy with coverage amounts based on their individual contribution to the business.'
            }
          }
        ]
      }
    ];

    schemas.forEach((schema, index) => {
      const script = document.createElement('script');
      script.id = `schema-blog-key-person-${index}`;
      script.type = 'application/ld+json';
      script.text = JSON.stringify(schema);
      document.head.appendChild(script);
    });

    // Canonical URL
    let canonical = document.querySelector('link[rel="canonical"]') as HTMLLinkElement | null;
    if (!canonical) {
      canonical = document.createElement('link');
      canonical.setAttribute('rel', 'canonical');
      document.head.appendChild(canonical);
    }
    canonical.setAttribute('href', `${SITE_URL}/blog/business-key-person-insurance-indianapolis`);

    // Document title & meta description
    document.title = 'Key Person Insurance Indianapolis: Protect Your Business | Hoosier Life Insurance';
    const descMeta = document.querySelector('meta[name="description"]') as HTMLMetaElement | null || (() => { const m = document.createElement('meta'); m.setAttribute('name','description'); document.head.appendChild(m); return m; })();
    descMeta.setAttribute('content', 'Key person insurance for Indianapolis businesses. Protect your company if a critical employee passes away. Free quote from a local Indiana specialist.');

    const setOgMeta = (property: string, content: string) => {
      let meta = document.querySelector(`meta[property="${property}"]`) as HTMLMetaElement | null;
      if (!meta) {
        meta = document.createElement('meta');
        meta.setAttribute('property', property);
        document.head.appendChild(meta);
      }
      meta.setAttribute('content', content);
    };
    const setNameMeta = (name: string, content: string) => {
      let meta = document.querySelector(`meta[name="${name}"]`) as HTMLMetaElement | null;
      if (!meta) {
        meta = document.createElement('meta');
        meta.setAttribute('name', name);
        document.head.appendChild(meta);
      }
      meta.setAttribute('content', content);
    };
    setOgMeta('og:type', 'article');
    setOgMeta('og:title', 'Key Person Insurance Indianapolis: Protect Your Business | Hoosier Life');
    setOgMeta('og:description', 'Key person insurance for Indianapolis businesses. Protect your company if a critical employee passes away.');
    setOgMeta('og:url', `${SITE_URL}/blog/business-key-person-insurance-indianapolis`);
    setOgMeta('og:image', 'https://readdy.ai/api/search-image?query=Indianapolis%20business%20owner%20and%20key%20employee%20team%20in%20modern%20office%20reviewing%20business%20insurance%20protection%20documents%20professional%20setting%20clean%20bright%20background%20trust%20partnership&width=1200&height=630&seq=og-key-person-indy&orientation=landscape');
    setNameMeta('twitter:card', 'summary_large_image');
    setNameMeta('twitter:image', 'https://readdy.ai/api/search-image?query=Indianapolis%20business%20owner%20and%20key%20employee%20team%20in%20modern%20office%20reviewing%20business%20insurance%20protection%20documents%20professional%20setting%20clean%20bright%20background%20trust%20partnership&width=1200&height=630&seq=og-key-person-indy&orientation=landscape');

    return () => {
      schemas.forEach((_, index) => {
        const script = document.getElementById(`schema-blog-key-person-${index}`);
        if (script) script.remove();
      });
    };
  }, [SITE_URL]);

  return (
    <>
      <title>Key Person Insurance Indianapolis: Protect Your Business | Hoosier Life</title>
      <meta name="description" content="How key person life insurance protects Indianapolis businesses — costs, coverage amounts, and how to structure a policy for your company." />
      <link rel="canonical" href="https://hoosierlifeinsurance.com/blog/business-key-person-insurance-indianapolis" />
      <meta name="robots" content="index, follow" />
      <div className="min-h-screen bg-white">
        <BlogProgressBar />
        <Header />
        
        {/* Intro */}
        <div className="prose prose-lg max-w-none mb-12">
          <p className="text-lg leading-relaxed text-gray-700">
            What would happen to your Indianapolis business if your top salesperson, lead engineer, or co-founder suddenly passed away? Key person insurance (also called key man insurance) protects your business from the financial devastation of losing an essential employee. This guide covers everything Indianapolis business owners need to know about key person coverage, from calculating the right amount to understanding tax implications.
          </p>
        </div>

        {/* Breadcrumb */}
        <nav className="bg-gray-50 border-b border-gray-200">
          <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 py-4">
            <ol className="flex items-center space-x-2 text-sm">
              <li><Link to="/" className="text-gray-500 hover:text-[#D97642]">Home</Link></li>
              <li className="text-gray-400">/</li>
              <li><Link to="/blog" className="text-gray-500 hover:text-[#D97642]">Blog</Link></li>
              <li className="text-gray-400">/</li>
              <li className="text-gray-900">Business Key Person Insurance</li>
            </ol>
          </div>
        </nav>

        {/* Article */}
        <article className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 py-12">
          {/* Article Header */}
          <header className="mb-8">
            <h1 className="text-4xl font-bold text-gray-900 mb-4">
              Key Person Insurance for Indianapolis Businesses: Complete Guide
            </h1>
            <div className="flex items-center space-x-6 text-sm text-gray-600">
              <span className="flex items-center">
                <i className="ri-calendar-line mr-2"></i>
                March 2025
              </span>
              <span className="flex items-center">
                <i className="ri-time-line mr-2"></i>
                11 min read
              </span>
              <span className="flex items-center">
                <i className="ri-map-pin-line mr-2"></i>
                Indianapolis, IN
              </span>
              <span className="flex items-center">
                <i className="ri-shield-check-line mr-2"></i>
                Licensed Indiana Specialists
              </span>
            </div>
          </header>

          {/* Key Takeaways */}
          <div className="bg-[#1A3A52] text-white rounded-2xl p-7 mb-10">
            <h2 className="text-lg font-bold mb-4 flex items-center gap-2">
              <i className="ri-bookmark-3-fill text-[#D97642] text-xl"></i>
              Key Takeaways
            </h2>
            <ul className="space-y-2.5">
              {[
                'Key person insurance pays your Indianapolis business a lump sum if a critical owner or employee dies — providing runway to recruit, rebuild, and survive the transition',
                'Coverage should equal 5–10× the key person\'s annual salary, or 2–3× their annual revenue contribution — whichever is larger',
                'A healthy 35-year-old key employee can be insured for as little as $25–$35/month ($500K/10yr term) — a fraction of their value to the business',
                'The death benefit is received tax-free by the business; however, key person insurance premiums are generally NOT tax-deductible',
                'SBA loans and many business lines of credit require key person insurance as a lender condition — it\'s often non-negotiable for business financing',
              ].map((point, i) => (
                <li key={i} className="flex items-start gap-3 text-sm text-white/90">
                  <i className="ri-check-line text-[#D97642] mt-0.5 flex-shrink-0"></i>
                  {point}
                </li>
              ))}
            </ul>
          </div>

          {/* What is Key Person Insurance */}
          <section className="mb-12">
            <h2 className="text-3xl font-bold text-gray-900 mb-4">What is Key Person Insurance?</h2>
            <div className="bg-gray-50 border-l-4 border-[#D97642] p-4 rounded-r-lg mb-5 text-gray-800 text-sm leading-relaxed">
              <strong>Direct answer:</strong> Key person insurance is a life insurance policy your business owns on a critical employee or owner. The business pays the premiums and receives the tax-free death benefit if that person dies. The funds cover lost revenue, recruitment and training costs, business loan obligations, and the runway needed to transition without financial collapse.
            </div>
            <div className="bg-gray-50 rounded-lg p-6 mb-6">
              <p className="text-gray-700 mb-4">
                Key person insurance is a life insurance policy your business owns on a critical employee. If that person dies, your business receives the death benefit to cover:
              </p>
              <div className="grid md:grid-cols-2 gap-6 mt-6">
                <div className="bg-white rounded-lg p-6 border border-gray-200">
                  <div className="w-12 h-12 bg-[#D97642]/10 rounded-lg flex items-center justify-center mb-4">
                    <i className="ri-money-dollar-circle-line text-2xl text-[#D97642]"></i>
                  </div>
                  <h3 className="text-lg font-semibold text-gray-900 mb-2">Lost Revenue</h3>
                  <p className="text-gray-600">Replace income from clients or projects the key person managed.</p>
                </div>
                <div className="bg-white rounded-lg p-6 border border-gray-200">
                  <div className="w-12 h-12 bg-[#D97642]/10 rounded-lg flex items-center justify-center mb-4">
                    <i className="ri-team-line text-2xl text-[#D97642]"></i>
                  </div>
                  <h3 className="text-lg font-semibold text-gray-900 mb-2">Recruitment Costs</h3>
                  <p className="text-gray-600">Pay for hiring, training, and onboarding a replacement.</p>
                </div>
                <div className="bg-white rounded-lg p-6 border border-gray-200">
                  <div className="w-12 h-12 bg-[#D97642]/10 rounded-lg flex items-center justify-center mb-4">
                    <i className="ri-bank-line text-2xl text-[#D97642]"></i>
                  </div>
                  <h3 className="text-lg font-semibold text-gray-900 mb-2">Debt Obligations</h3>
                  <p className="text-gray-600">Continue paying business loans, rent, and payroll during transition.</p>
                </div>
                <div className="bg-white rounded-lg p-6 border border-gray-200">
                  <div className="w-12 h-12 bg-[#D97642]/10 rounded-lg flex items-center justify-center mb-4">
                    <i className="ri-line-chart-line text-2xl text-[#D97642]"></i>
                  </div>
                  <h3 className="text-lg font-semibold text-gray-900 mb-2">Investor Confidence</h3>
                  <p className="text-gray-600">Reassure investors and lenders that the business can survive.</p>
                </div>
              </div>
            </div>
          </section>

          {/* Who Needs Key Person Insurance */}
          <section className="mb-12">
            <h2 className="text-3xl font-bold text-gray-900 mb-4">Who Needs Key Person Insurance?</h2>
            <div className="bg-gray-50 border-l-4 border-[#D97642] p-4 rounded-r-lg mb-5 text-gray-800 text-sm leading-relaxed">
              <strong>Direct answer:</strong> Any Indianapolis business where one person's death would materially harm revenue, operations, or loan repayment capacity needs key person coverage. This includes startups where the founder is also the primary revenue generator, businesses with SBA loans (lenders often require it), professional services firms (law, accounting, medicine) built on personal client relationships, and companies with a single top salesperson generating 40%+ of revenue.
            </div>
            <p className="text-gray-700 mb-6">
              Any Indianapolis business that depends heavily on one or more individuals should consider key person coverage:
            </p>
            <div className="space-y-4">
              <div className="bg-white border-l-4 border-[#D97642] p-6 rounded-lg">
                <h3 className="text-xl font-semibold text-gray-900 mb-2">Startups &amp; Small Businesses</h3>
                <p className="text-gray-700">
                  If your founder, CEO, or lead developer is the face of the company or holds critical technical knowledge, their loss could shut down operations.
                </p>
              </div>
              <div className="bg-white border-l-4 border-[#D97642] p-6 rounded-lg">
                <h3 className="text-xl font-semibold text-gray-900 mb-2">Sales-Driven Companies</h3>
                <p className="text-gray-700">
                  If one salesperson generates 40%+ of your revenue, losing them could cripple cash flow. Key person insurance replaces that income while you rebuild.
                </p>
              </div>
              <div className="bg-white border-l-4 border-[#D97642] p-6 rounded-lg">
                <h3 className="text-xl font-semibold text-gray-900 mb-2">Professional Services</h3>
                <p className="text-gray-700">
                  Law firms, accounting practices, medical offices, and consulting firms often depend on one or two partners who bring in most clients.
                </p>
              </div>
              <div className="bg-white border-l-4 border-[#D97642] p-6 rounded-lg">
                <h3 className="text-xl font-semibold text-gray-900 mb-2">Businesses with Loans</h3>
                <p className="text-gray-700">
                  If you have an SBA loan or business line of credit, lenders may require key person insurance to protect their investment.
                </p>
              </div>
            </div>
          </section>

          {/* How Much Coverage Do You Need */}
          <section className="mb-12">
            <h2 className="text-3xl font-bold text-gray-900 mb-6">How Much Coverage Do You Need?</h2>
            <p className="text-gray-700 mb-6">
              Most businesses calculate key person coverage using one of these methods:
            </p>
            <div className="overflow-x-auto mb-6">
              <table className="w-full border-collapse border border-gray-300">
                <thead>
                  <tr className="bg-gray-100">
                    <th className="border border-gray-300 px-4 py-3 text-left font-semibold text-gray-900">Calculation Method</th>
                    <th className="border border-gray-300 px-4 py-3 text-left font-semibold text-gray-900">Formula</th>
                    <th className="border border-gray-300 px-4 py-3 text-left font-semibold text-gray-900">Best For</th>
                  </tr>
                </thead>
                <tbody>
                  <tr>
                    <td className="border border-gray-300 px-4 py-3 text-gray-700">Income Multiplier</td>
                    <td className="border border-gray-300 px-4 py-3 text-gray-700">5-10x annual salary</td>
                    <td className="border border-gray-300 px-4 py-3 text-gray-700">General employees</td>
                  </tr>
                  <tr className="bg-gray-50">
                    <td className="border border-gray-300 px-4 py-3 text-gray-700">Revenue Contribution</td>
                    <td className="border border-gray-300 px-4 py-3 text-gray-700">2-3x annual revenue generated</td>
                    <td className="border border-gray-300 px-4 py-3 text-gray-700">Sales leaders</td>
                  </tr>
                  <tr>
                    <td className="border border-gray-300 px-4 py-3 text-gray-700">Replacement Cost</td>
                    <td className="border border-gray-300 px-4 py-3 text-gray-700">Recruiting + training + lost productivity</td>
                    <td className="border border-gray-300 px-4 py-3 text-gray-700">Specialized roles</td>
                  </tr>
                  <tr className="bg-gray-50">
                    <td className="border border-gray-300 px-4 py-3 text-gray-700">Debt Coverage</td>
                    <td className="border border-gray-300 px-4 py-3 text-gray-700">Outstanding business loans + 1 year operating expenses</td>
                    <td className="border border-gray-300 px-4 py-3 text-gray-700">Founders with debt</td>
                  </tr>
                </tbody>
              </table>
            </div>
            <div className="bg-blue-50 border-l-4 border-blue-500 p-6 rounded">
              <p className="text-blue-900">
                <strong>Example:</strong> A top salesperson earning $80,000/year who generates $500,000 in annual revenue should have $1,000,000-$1,500,000 in key person coverage (2-3x their revenue contribution).
              </p>
            </div>
          </section>

          {/* Cost Examples */}
          <section className="mb-12">
            <h2 className="text-3xl font-bold text-gray-900 mb-6">Cost Examples for Indianapolis Businesses</h2>
            <p className="text-gray-700 mb-6">
              Key person insurance costs depend on the insured person's age, health, and coverage amount:
            </p>
            <div className="overflow-x-auto mb-6">
              <table className="w-full border-collapse border border-gray-300">
                <thead>
                  <tr className="bg-gray-100">
                    <th className="border border-gray-300 px-4 py-3 text-left font-semibold text-gray-900">Key Person Age</th>
                    <th className="border border-gray-300 px-4 py-3 text-left font-semibold text-gray-900">Coverage Amount</th>
                    <th className="border border-gray-300 px-4 py-3 text-left font-semibold text-gray-900">Term Length</th>
                    <th className="border border-gray-300 px-4 py-3 text-left font-semibold text-gray-900">Monthly Cost</th>
                  </tr>
                </thead>
                <tbody>
                  <tr>
                    <td className="border border-gray-300 px-4 py-3 text-gray-700">35-year-old</td>
                    <td className="border border-gray-300 px-4 py-3 text-gray-700">$500,000</td>
                    <td className="border border-gray-300 px-4 py-3 text-gray-700">10 years</td>
                    <td className="border border-gray-300 px-4 py-3 font-semibold text-[#D97642]">$25-35/month</td>
                  </tr>
                  <tr className="bg-gray-50">
                    <td className="border border-gray-300 px-4 py-3 text-gray-700">40-year-old</td>
                    <td className="border border-gray-300 px-4 py-3 text-gray-700">$1,000,000</td>
                    <td className="border border-gray-300 px-4 py-3 text-gray-700">15 years</td>
                    <td className="border border-gray-300 px-4 py-3 font-semibold text-[#D97642]">$80-110/month</td>
                  </tr>
                  <tr>
                    <td className="border border-gray-300 px-4 py-3 text-gray-700">50-year-old</td>
                    <td className="border border-gray-300 px-4 py-3 text-gray-700">$750,000</td>
                    <td className="border border-gray-300 px-4 py-3 text-gray-700">10 years</td>
                    <td className="border border-gray-300 px-4 py-3 font-semibold text-[#D97642]">$120-160/month</td>
                  </tr>
                  <tr className="bg-gray-50">
                    <td className="border border-gray-300 px-4 py-3 text-gray-700">55-year-old</td>
                    <td className="border border-gray-300 px-4 py-3 text-gray-700">$500,000</td>
                    <td className="border border-gray-300 px-4 py-3 text-gray-700">10 years</td>
                    <td className="border border-gray-300 px-4 py-3 font-semibold text-[#D97642]">$110-145/month</td>
                  </tr>
                </tbody>
              </table>
            </div>
          </section>

          {/* Tax Implications */}
          <section className="mb-12">
            <h2 className="text-3xl font-bold text-gray-900 mb-4">Tax Implications of Key Person Insurance</h2>
            <div className="bg-gray-50 border-l-4 border-[#D97642] p-4 rounded-r-lg mb-5 text-gray-800 text-sm leading-relaxed">
              <strong>Direct answer:</strong> Premiums for key person insurance are NOT tax-deductible for Indianapolis businesses — the IRS classifies the business as both owner and beneficiary. However, the death benefit IS received income-tax-free under IRC Section 101. Consult a CPA if you're considering using permanent life insurance for key person purposes, as the tax rules become more complex.
            </div>
            <div className="bg-yellow-50 border-l-4 border-yellow-500 p-6 rounded mb-6">
              <p className="text-yellow-900">
                <strong>Important:</strong> Key person insurance has unique tax treatment. Consult with a CPA or tax advisor before purchasing.
              </p>
            </div>
            <div className="grid md:grid-cols-2 gap-6">
              <div className="bg-white border border-gray-200 rounded-lg p-6">
                <div className="w-12 h-12 bg-red-100 rounded-lg flex items-center justify-center mb-4">
                  <i className="ri-close-circle-line text-2xl text-red-600"></i>
                </div>
                <h3 className="text-lg font-semibold text-gray-900 mb-2">Premiums Are NOT Tax-Deductible</h3>
                <p className="text-gray-600">
                  Unlike health insurance or other business expenses, you cannot deduct key person insurance premiums as a business expense.
                </p>
              </div>
              <div className="bg-white border border-gray-200 rounded-lg p-6">
                <div className="w-12 h-12 bg-green-100 rounded-lg flex items-center justify-center mb-4">
                  <i className="ri-check-circle-line text-2xl text-green-600"></i>
                </div>
                <h3 className="text-lg font-semibold text-gray-900 mb-2">Death Benefits Are Tax-Free</h3>
                <p className="text-gray-600">
                  The death benefit your business receives is generally income tax-free, giving you the full amount to cover losses.
                </p>
              </div>
            </div>
          </section>

          {/* Author Bio */}
          <div className="border border-gray-200 rounded-2xl p-7 my-8 flex items-start gap-5">
            <div className="w-14 h-14 flex items-center justify-center bg-[#1A3A52] rounded-full flex-shrink-0">
              <i className="ri-shield-user-line text-white text-2xl"></i>
            </div>
            <div>
              <p className="font-bold text-gray-900 text-base mb-1">Written by the Licensed Life Insurance Specialists at Hoosier Life Insurance</p>
              <p className="text-sm text-gray-600 leading-relaxed mb-2">Our licensed Indiana agents help Indianapolis business owners design key person insurance strategies, buy-sell agreement funding, and business continuation planning. We work with Principal, Pacific Life, Protective, and other carriers experienced in business coverage.</p>
              <div className="flex flex-wrap gap-3 text-xs text-gray-500">
                <span className="flex items-center gap-1"><i className="ri-checkbox-circle-line text-[#D97642]"></i>Licensed in Indiana</span>
                <span className="flex items-center gap-1"><i className="ri-checkbox-circle-line text-[#D97642]"></i>Independent — 20+ Carriers</span>
                <span className="flex items-center gap-1"><i className="ri-checkbox-circle-line text-[#D97642]"></i>Last reviewed March 2025</span>
              </div>
            </div>
          </div>

          {/* FAQ Section */}
          <section className="mb-12">
            <h2 className="text-3xl font-bold text-gray-900 mb-6">Frequently Asked Questions</h2>
            <div className="space-y-4">
              <details className="bg-gray-50 rounded-lg p-6 cursor-pointer">
                <summary className="font-semibold text-gray-900 text-lg">Does the key person need to consent to the policy?</summary>
                <p className="mt-4 text-gray-700">
                  Yes. The insured person must consent to the policy, undergo a medical exam, and sign the application. They have no ownership rights to the policy — the business owns it, pays premiums, and receives the death benefit.
                </p>
              </details>
              <details className="bg-gray-50 rounded-lg p-6 cursor-pointer">
                <summary className="font-semibold text-gray-900 text-lg">What happens if the key person leaves the company?</summary>
                <p className="mt-4 text-gray-700">
                  You have several options: (1) Cancel the policy and stop paying premiums, (2) Convert it to a policy on a different key employee (some carriers allow this), or (3) Transfer ownership to the departing employee as part of their severance package. Most businesses cancel the policy when the person leaves.
                </p>
              </details>
              <details className="bg-gray-50 rounded-lg p-6 cursor-pointer">
                <summary className="font-semibold text-gray-900 text-lg">Can I have key person insurance on multiple employees?</summary>
                <p className="mt-4 text-gray-700">
                  Absolutely. Many businesses insure 2-5 key people — founders, top salespeople, lead engineers, etc. Each person gets their own policy with coverage amounts based on their individual contribution to the business.
                </p>
              </details>
            </div>
          </section>

          {/* CTA Section */}
          <section className="bg-gradient-to-br from-[#1A3A52] to-[#0F2A3F] rounded-xl p-8 text-center text-white mb-12">
            <h2 className="text-3xl font-bold mb-4">Protect Your Indianapolis Business</h2>
            <p className="text-xl mb-6 text-white/80">
              Get a free key person insurance quote and safeguard your company's future.
            </p>
            <div className="flex flex-col sm:flex-row gap-4 justify-center">
              <Link
                to="/instant-quote"
                className="inline-flex items-center justify-center px-8 py-4 bg-white text-[#1A3A52] rounded-lg font-semibold hover:bg-gray-100 transition-colors whitespace-nowrap"
              >
                <i className="ri-calculator-line mr-2"></i>
                Get Free Quote
              </Link>
              <Link
                to="/book-appointment"
                className="inline-flex items-center justify-center px-8 py-4 bg-[#0F2A3F] text-white rounded-lg font-semibold hover:bg-[#0A1F2E] transition-colors whitespace-nowrap"
              >
                <i className="ri-calendar-line mr-2"></i>
                Book Consultation
              </Link>
            </div>
          </section>
        </article>

        {/* Mid-Article Quiz CTA */}
        <section className="py-12 bg-gradient-to-br from-orange-50 to-amber-50">
          <div className="max-w-4xl mx-auto px-6 text-center">
            <div className="inline-flex items-center justify-center w-16 h-16 bg-orange-100 rounded-full mb-4">
              <i className="ri-question-line text-3xl text-orange-600"></i>
            </div>
            <h3 className="text-2xl font-bold text-gray-900 mb-3">
              Not Sure Which Policy Is Right for You?
            </h3>
            <p className="text-lg text-gray-600 mb-6 max-w-2xl mx-auto">
              Take our 2-minute quiz to get a personalized recommendation based on your family's unique needs and budget.
            </p>
            <a
              href="/quiz"
              className="inline-flex items-center gap-2 bg-orange-600 text-white px-8 py-4 rounded-lg font-semibold hover:bg-orange-700 transition-colors shadow-lg hover:shadow-xl whitespace-nowrap"
            >
              Take the Quiz Now
              <i className="ri-arrow-right-line"></i>
            </a>
          </div>
        </section>

        <BlogRelatedStrip currentSlug="business-key-person-insurance-indianapolis" />
        <Footer />
      </div>
    </>
  );
}